/**
 * Tema (claro/escuro) da Plataforma — helpers PUROS e testáveis.
 *
 * O pré-paint acontece no script inline do index.html (evita FOUC/CLS). Este
 * módulo centraliza a LÓGICA (resolver tema inicial, aplicar no root, alternar)
 * para que o toggle do shell e os testes usem a mesma verdade, sem tocar o DOM
 * no import (nada roda fora das funções).
 *
 * Contrato de persistência: localStorage['super2-theme'] guarda só "light"/
 * "dark". Qualquer outro valor (ou ausência) cai na preferência do SO.
 */
export type Theme = "light" | "dark";

/** Chave única de persistência do tema (mesma string do script do index.html). */
export const THEME_STORAGE_KEY = "super2-theme";

/** Normaliza um valor arbitrário de storage para um tema válido, ou null. */
export function normalizeStoredTheme(value: string | null | undefined): Theme | null {
  return value === "light" || value === "dark" ? value : null;
}

/**
 * Tema inicial: o valor salvo pelo usuário VENCE a preferência do SO; sem valor
 * salvo, segue `prefersDark` (prefers-color-scheme). Default conservador: light.
 */
export function resolveInitialTheme(stored: string | null | undefined, prefersDark: boolean): Theme {
  const normalized = normalizeStoredTheme(stored);
  if (normalized) return normalized;
  return prefersDark ? "dark" : "light";
}

/** Inverte o tema atual. */
export function toggleTheme(current: Theme): Theme {
  return current === "dark" ? "light" : "dark";
}

/**
 * Aplica o tema no elemento raiz (normalmente <html>) via data-theme — o MESMO
 * seletor que styles/tokens.css e o Tailwind (`darkMode`) usam. Mantém o
 * `color-scheme` coerente para que controles nativos (scrollbar, inputs) sigam
 * o tema.
 */
export function applyTheme(root: HTMLElement, theme: Theme): void {
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
}

/** Lê o tema atualmente aplicado no root (fallback light). */
export function currentTheme(root: HTMLElement): Theme {
  return root.dataset.theme === "dark" ? "dark" : "light";
}

/** Persiste a escolha do usuário; falha de storage (modo privado) é ignorada. */
export function persistTheme(theme: Theme, storage: Pick<Storage, "setItem"> | null | undefined): void {
  try {
    storage?.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage indisponível (modo privado/bloqueado): o tema ainda funciona na
    // sessão via data-theme; só não persiste. Não é erro operacional.
  }
}
